import { useState, useEffect, useCallback } from "react";
import { useDataProvider, useNotify } from "ra-core";
import { Plus, Copy, ArrowUpDown, X, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DEFAULT_PLANOS,
  type CriterioConfig,
  type PlanoConfig,
} from "./defaultParametrizacao";

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
type Atividade = "servicos" | "comercios" | "industrias";
type Regime = "simples" | "presumido" | "real" | "imunes";

interface FaixaFaturamento {
  id: string;
  de: string;
  ate: string | null; // null = "ou mais"
  valor: string;
}

interface RegimeTabela {
  faixas: FaixaFaturamento[];
}

type TabelaFaturamento = Record<Atividade, Record<Regime, RegimeTabela>>;

interface CriterioAdicional {
  id: string;
  nome: string;
  descricao: string;
  tipo: "faixa" | "boolean" | "quantidade";
  planos: string[]; // basico | intermediario | avancado
  vincularPlanos: boolean;
  faixas: Array<{ id: string; label: string; valor: string }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT DATA
// ─────────────────────────────────────────────────────────────────────────────
const REGIMES: Array<{ key: Regime; label: string }> = [
  { key: "simples", label: "SIMPLES NACIONAL" },
  { key: "presumido", label: "LUCRO PRESUMIDO" },
  { key: "real", label: "LUCRO REAL" },
  { key: "imunes", label: "IMUNES OU ISENTAS" },
];

const PLANO_LABELS: Record<string, string> = {
  basico: "BÁSICO",
  intermediario: "INTERMEDIÁRIO (RECOMENDADO)",
  avancado: "AVANÇADO",
};

function makeFaixas(
  data: Array<[string, string | null, string]>,
): FaixaFaturamento[] {
  return data.map(([de, ate, valor], i) => ({ id: `f${i}`, de, ate, valor }));
}

const DEFAULT_TABELA: TabelaFaturamento = {
  servicos: {
    simples: {
      faixas: makeFaixas([
        ["0", "6.750,00", "198,00"],
        ["6.750,01", "15.000,00", "267,00"],
        ["15.000,01", "25.000,00", "387,00"],
        ["25.000,01", "50.000,00", "547,00"],
        ["50.000,01", "100.000,00", "747,00"],
        ["100.000,01", "200.000,00", "1.147,00"],
        ["200.000,01", "400.000,00", "1.289,00"],
        ["400.000,01", null, "0,00"],
      ]),
    },
    presumido: {
      faixas: makeFaixas([
        ["0", "15.000,00", "327,00"],
        ["15.000,01", "25.000,00", "457,00"],
        ["25.000,01", "50.000,00", "597,00"],
        ["50.000,01", "100.000,00", "897,00"],
        ["100.000,01", "200.000,00", "1.297,00"],
        ["200.000,01", "300.000,00", "1.997,00"],
        ["300.000,01", "500.000,00", "2.297,00"],
        ["500.000,01", "800.000,00", "4.115,00"],
        ["800.000,01", "1.500.000,00", "5.225,00"],
        ["1.500.000,01", "2.500.000,00", "7.644,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    real: {
      faixas: makeFaixas([
        ["0", "100.000,00", "810,00"],
        ["100.000,01", "300.000,00", "1.816,00"],
        ["300.000,01", "500.000,00", "2.899,00"],
        ["500.000,01", "800.000,00", "4.289,00"],
        ["800.000,01", "1.500.000,00", "6.489,00"],
        ["1.500.000,01", "2.500.000,00", "7.891,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    imunes: {
      faixas: makeFaixas([
        ["0", "100.000", "392,00"],
        ["100.000,01", "300.000,00", "1.831,00"],
        ["300.000,01", "500.000,00", "4.009,00"],
        ["500.000,01", "800.000,00", "4.752,00"],
        ["800.000,01", "1.500.000,00", "5.739,00"],
        ["1.500.000,01", "2.500.000,00", "7.721,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
  },
  comercios: {
    simples: {
      faixas: makeFaixas([
        ["0", "6.750,00", "299,00"],
        ["6.750,01", "20.000,00", "397,00"],
        ["20.000,01", "50.000,00", "497,00"],
        ["50.000,01", "100.000,00", "828,00"],
        ["100.000,01", "200.000,00", "1.313,00"],
        ["200.000,01", "300.000,00", "1.618,00"],
        ["300.000,01", "400.000,00", "2.350,00"],
        ["400.000,01", null, "0,00"],
      ]),
    },
    presumido: {
      faixas: makeFaixas([
        ["0", "20.000,00", "497,00"],
        ["20.000,01", "50.000,00", "597,00"],
        ["50.000,01", "100.000,00", "1.008,00"],
        ["100.000,01", "300.000,00", "2.484,00"],
        ["300.000,01", "400.000,00", "3.306,00"],
        ["400.000,01", "500.000,00", "6.790,00"],
        ["500.000,01", "800.000,00", "6.292,00"],
        ["800.000,01", "1.500.000,00", "7.935,00"],
        ["1.500.000,01", "2.500.000,00", "11.220,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    real: {
      faixas: makeFaixas([
        ["0", "100.000,00", "1.147,00"],
        ["100.000,01", "300.000,00", "2.088,00"],
        ["300.000,01", "400.000,00", "3.798,00"],
        ["400.000,01", "500.000,00", "4.989,00"],
        ["500.000,01", "800.000,00", "5.980,00"],
        ["800.000,01", "800.000,00", "6.516,00"],
        ["800.000,01", "1.500.000,00", "8.201,00"],
        ["1.500.000,01", "2.500.000,00", "12.100,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    imunes: {
      faixas: makeFaixas([
        ["0", "100.000", "1.337,00"],
        ["100.000,01", "300.000,00", "2.436,00"],
        ["300.000,01", "500.000,00", "4.811,00"],
        ["500.000,01", "800.000,00", "5.702,00"],
        ["800.000,01", "1.500.000,00", "6.755,00"],
        ["1.500.000,01", "2.500.000,00", "9.265,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
  },
  industrias: {
    simples: {
      faixas: makeFaixas([
        ["0", "50.000,00", "725,00"],
        ["50.000,01", "100.000,00", "1.150,00"],
        ["100.000,01", "200.000,00", "1.636,00"],
        ["200.000,01", "600.000,00", "1.850,00"],
        ["400.000,01", null, "0,00"],
      ]),
    },
    presumido: {
      faixas: makeFaixas([
        ["0", "100.000,00", "1.094,00"],
        ["100.000,01", "300.000", "2.813,00"],
        ["300.000,01", "500.000,00", "3.750,00"],
        ["500.000,01", "800.000,00", "4.800,00"],
        ["800.000,01", "1.500.000,00", "6.469,00"],
        ["1.500.000,01", "2.500.000,00", "7.188,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    real: {
      faixas: makeFaixas([
        ["0", "100.000,00", "1.138,00"],
        ["100.000,01", "300.000,00", "2.925,00"],
        ["300.000,01", "500.000,00", "3.906,00"],
        ["500.000,01", "800.000,00", "6.977,00"],
        ["800.000,01", "1.500.000,00", "6.750,00"],
        ["1.500.000,01", "2.500.000,00", "7.500,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
    imunes: {
      faixas: makeFaixas([
        ["0", "100.000", "1.337,00"],
        ["100.000,01", "300.000,00", "2.436,00"],
        ["300.000,01", "500.000,00", "4.811,00"],
        ["500.000,01", "800.000,00", "5.702,00"],
        ["800.000,01", "1.500.000,00", "6.755,00"],
        ["1.500.000,01", "2.500.000,00", "9.265,00"],
        ["2.500.000,01", null, "0,00"],
      ]),
    },
  },
};

const DEFAULT_CRITERIOS_ADICIONAIS: CriterioAdicional[] = [
  {
    id: "ca-colaboradores",
    nome: "Quantidade de Colaboradores",
    descricao:
      "Será multiplicado o valor em emprego pela total somando ao valor final do plano.",
    tipo: "faixa",
    planos: ["basico", "intermediario", "avancado"],
    vincularPlanos: true,
    faixas: [
      { id: "f1", label: "1 a 3", valor: "50,00" },
      { id: "f2", label: "4 a 6", valor: "40,00" },
      { id: "f3", label: "7 a 15", valor: "35,00" },
      { id: "f4", label: "15 a 30", valor: "32,00" },
      { id: "f5", label: "31 ou mais", valor: "0,00" },
    ],
  },
  {
    id: "ca-pro-labore",
    nome: "Quantidade de Pró-Labore/Sócios",
    descricao:
      "Será multiplicado o valor em emprego pela total somando ao valor final do plano.",
    tipo: "faixa",
    planos: ["basico", "intermediario", "avancado"],
    vincularPlanos: true,
    faixas: [
      { id: "f1", label: "1 a 3", valor: "30,00" },
      { id: "f2", label: "4 ou mais", valor: "22,00" },
    ],
  },
  {
    id: "ca-ponto-eletronico",
    nome: "Terá Ponto Eletrônico Integrado?",
    descricao:
      "Será somado o valor definido na opção escolhida ao valor final do plano.",
    tipo: "boolean",
    planos: ["basico", "intermediario", "avancado"],
    vincularPlanos: false,
    faixas: [
      { id: "f1", label: "SIM", valor: "0,00" },
      { id: "f2", label: "NÃO", valor: "50,00" },
    ],
  },
  {
    id: "ca-icms-st",
    nome: "ICMS-ST Operações Interestaduais",
    descricao:
      "Será somado o valor definido na opção escolhida ao valor final do plano.",
    tipo: "boolean",
    planos: ["basico", "intermediario", "avancado"],
    vincularPlanos: false,
    faixas: [
      { id: "f1", label: "SIM", valor: "100,00" },
      { id: "f2", label: "NÃO", valor: "0,00" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: generate unique ID
// ─────────────────────────────────────────────────────────────────────────────
function uid() {
  return Math.random().toString(36).slice(2, 9);
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Single faixa row in regime column */
function FaixaRow({
  faixa,
  onChangeDe: _onChangeDe,
  onChangeValor,
  onRemove,
}: {
  faixa: FaixaFaturamento;
  onChangeDe: (v: string) => void;
  onChangeValor: (v: string) => void;
  onRemove: () => void;
}) {
  const label = faixa.ate
    ? `${faixa.de} a ${faixa.ate}`
    : `${faixa.de} ou mais`;
  return (
    <div className="flex items-center gap-1 py-1 border-b border-border/40 last:border-0">
      {/* faixa label input (read-only display, editable if needed) */}
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <span className="text-muted-foreground text-[10px] shrink-0">R$</span>
        <Input
          value={label}
          readOnly
          className="h-7 text-[11px] bg-transparent border-0 focus-visible:ring-0 px-1 text-foreground/80 cursor-default min-w-0 w-full"
        />
      </div>
      {/* valor */}
      <Input
        value={faixa.valor}
        onChange={(e) => onChangeValor(e.target.value)}
        className="h-7 w-20 text-[11px] text-right font-mono shrink-0"
      />
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

/** Column of regime with its faixas */
function RegimeColumn({
  regime,
  tabela,
  onChange,
}: {
  regime: { key: Regime; label: string };
  tabela: RegimeTabela;
  onChange: (t: RegimeTabela) => void;
}) {
  const addFaixa = () => {
    const newFaixa: FaixaFaturamento = {
      id: uid(),
      de: "0",
      ate: null,
      valor: "0,00",
    };
    onChange({ faixas: [...tabela.faixas, newFaixa] });
  };

  const removeFaixa = (id: string) => {
    onChange({ faixas: tabela.faixas.filter((f) => f.id !== id) });
  };

  const updateValor = (id: string, valor: string) => {
    onChange({
      faixas: tabela.faixas.map((f) => (f.id === id ? { ...f, valor } : f)),
    });
  };

  return (
    <div className="flex-1 min-w-0 border border-border/60 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-muted/50 px-3 py-2 flex items-center justify-between border-b border-border/60">
        <span className="text-[11px] font-bold text-foreground/80 tracking-wide">
          {regime.label}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Duplicar coluna"
            className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Copy className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={addFaixa}
            title="Adicionar faixa"
            className="w-5 h-5 flex items-center justify-center rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-1 px-3 py-1 bg-muted/20 border-b border-border/40">
        <span className="flex-1 text-[10px] font-semibold text-muted-foreground uppercase">
          FAIXA
        </span>
        <span className="w-20 text-[10px] font-semibold text-muted-foreground uppercase text-right shrink-0">
          VALOR
        </span>
        <span className="w-5 shrink-0" />
      </div>

      {/* Rows */}
      <div className="px-3 py-1">
        {tabela.faixas.map((faixa) => (
          <FaixaRow
            key={faixa.id}
            faixa={faixa}
            onChangeDe={(v) =>
              onChange({
                faixas: tabela.faixas.map((f) =>
                  f.id === faixa.id ? { ...f, de: v } : f,
                ),
              })
            }
            onChangeValor={(v) => updateValor(faixa.id, v)}
            onRemove={() => removeFaixa(faixa.id)}
          />
        ))}
      </div>
    </div>
  );
}

/** Seção de Faixas de Faturamento por atividade */
function FaixasFaturamentoSection({
  tabela,
  onChange,
}: {
  tabela: TabelaFaturamento;
  onChange: (t: TabelaFaturamento) => void;
}) {
  const [atividade, setAtividade] = useState<Atividade>("servicos");
  const [moeda, setMoeda] = useState("R$");
  const [ajuste, setAjuste] = useState("");

  const updateRegime = (regime: Regime, t: RegimeTabela) => {
    onChange({
      ...tabela,
      [atividade]: { ...tabela[atividade], [regime]: t },
    });
  };

  return (
    <div className="space-y-4">
      {/* Atividade tabs */}
      <div className="flex items-center justify-center gap-2">
        {(["servicos", "comercios", "industrias"] as Atividade[]).map((a) => {
          const labels: Record<Atividade, string> = {
            servicos: "SERVIÇOS",
            comercios: "COMÉRCIOS",
            industrias: "INDÚSTRIAS",
          };
          const active = atividade === a;
          return (
            <button
              key={a}
              type="button"
              onClick={() => setAtividade(a)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {labels[a]}
              <span
                className={`w-3 h-3 rounded-full border-2 ${
                  active
                    ? "border-white bg-white/40"
                    : "border-muted-foreground"
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* Atividade section */}
      {(["servicos", "comercios", "industrias"] as Atividade[]).map((a) => {
        if (atividade !== a) return null;
        const labels: Record<Atividade, string> = {
          servicos: "SERVIÇOS",
          comercios: "COMÉRCIOS",
          industrias: "INDÚSTRIAS",
        };
        return (
          <div key={a} className="space-y-3">
            <div className="text-center space-y-1">
              <h3 className="text-sm font-bold tracking-widest text-foreground">
                {labels[a]}
              </h3>
              <button
                type="button"
                className="text-[11px] text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                VINCULAR A PLANO(S)
              </button>
            </div>

            {/* Plan checkboxes */}
            <div className="flex items-center justify-center gap-6">
              {["basico", "intermediario", "avancado"].map((p) => (
                <label
                  key={p}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <Checkbox defaultChecked className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-medium text-foreground/80">
                    {PLANO_LABELS[p]}
                  </span>
                </label>
              ))}
            </div>

            {/* Ajustar valores */}
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
              >
                Ajustar Valores ⓘ
              </button>
            </div>

            {/* Currency + value + apply */}
            <div className="flex items-center justify-end gap-2 pr-1">
              <Select value={moeda} onValueChange={setMoeda}>
                <SelectTrigger className="h-7 w-14 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="R$" className="text-xs">
                    R$
                  </SelectItem>
                  <SelectItem value="%" className="text-xs">
                    %
                  </SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={ajuste}
                onChange={(e) => setAjuste(e.target.value)}
                placeholder="0,00"
                className="h-7 w-24 text-[11px] text-right font-mono"
              />
              <button
                type="button"
                className="h-7 w-7 flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                className="h-7 w-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 4 Regime columns */}
            <div className="flex gap-3 overflow-x-auto pb-1">
              {REGIMES.map((regime) => (
                <RegimeColumn
                  key={regime.key}
                  regime={regime}
                  tabela={tabela[a][regime.key]}
                  onChange={(t) => updateRegime(regime.key, t)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Critério Adicional card */
function CriterioCard({
  criterio,
  onChange,
  onDelete,
}: {
  criterio: CriterioAdicional;
  onChange: (c: CriterioAdicional) => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(true);

  const togglePlano = (p: string) => {
    const planos = criterio.planos.includes(p)
      ? criterio.planos.filter((x) => x !== p)
      : [...criterio.planos, p];
    onChange({ ...criterio, planos });
  };

  const addFaixa = () => {
    onChange({
      ...criterio,
      faixas: [...criterio.faixas, { id: uid(), label: "", valor: "0,00" }],
    });
  };

  const removeFaixa = (id: string) => {
    onChange({
      ...criterio,
      faixas: criterio.faixas.filter((f) => f.id !== id),
    });
  };

  const updateFaixa = (id: string, field: "label" | "valor", value: string) => {
    onChange({
      ...criterio,
      faixas: criterio.faixas.map((f) =>
        f.id === id ? { ...f, [field]: value } : f,
      ),
    });
  };

  return (
    <div className="border border-border/60 rounded-lg overflow-hidden bg-card">
      {/* Top bar with +/- */}
      <div className="flex items-center justify-end gap-1 px-3 pt-2 pb-0">
        <button
          type="button"
          className="w-5 h-5 flex items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
          onClick={addFaixa}
          title="Adicionar faixa"
        >
          <Plus className="w-3 h-3" />
        </button>
        <button
          type="button"
          className="w-5 h-5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/80 transition-colors"
          onClick={onDelete}
          title="Remover critério"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <Collapsible open={open} onOpenChange={setOpen}>
        {/* Vincular a plano(s) + checkboxes */}
        <div className="px-4 py-2 border-b border-border/40 space-y-2">
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="text-[11px] text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
              onClick={() =>
                onChange({
                  ...criterio,
                  vincularPlanos: !criterio.vincularPlanos,
                })
              }
            >
              VINCULAR A PLANO(S)
            </button>
          </div>
          <div className="flex items-center justify-center gap-6">
            {["basico", "intermediario", "avancado"].map((p) => (
              <label
                key={p}
                className="flex items-center gap-1.5 cursor-pointer"
              >
                <Checkbox
                  checked={criterio.planos.includes(p)}
                  onCheckedChange={() => togglePlano(p)}
                  className="w-3.5 h-3.5"
                />
                <span className="text-[11px] font-medium text-foreground/80">
                  {PLANO_LABELS[p]}
                </span>
              </label>
            ))}
          </div>
        </div>

        <CollapsibleTrigger asChild>
          <div className="px-4 py-3 flex items-start justify-between cursor-pointer hover:bg-muted/20 transition-colors">
            <div className="space-y-0.5">
              <h4 className="text-sm font-semibold text-foreground">
                {criterio.nome}
              </h4>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {criterio.descricao}
              </p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground mt-0.5 shrink-0 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-3">
            {/* Table header */}
            <div className="flex items-center gap-2 py-1.5 border-b border-border/60">
              <span className="flex-1 text-[10px] font-semibold text-muted-foreground uppercase">
                {criterio.tipo === "boolean"
                  ? "OPÇÃO / DADO"
                  : "QUANTIDADE FAIXA / FAIXA RECORRENTE"}
              </span>
              <span className="w-24 text-[10px] font-semibold text-muted-foreground uppercase text-right shrink-0">
                {criterio.tipo === "faixa" ? "MULT" : "VALOR"}
              </span>
              <span className="w-5 shrink-0" />
            </div>

            {/* Rows */}
            {criterio.faixas.map((faixa) => (
              <div
                key={faixa.id}
                className="flex items-center gap-2 py-1.5 border-b border-border/30 last:border-0"
              >
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <span className="text-muted-foreground text-[10px] shrink-0">
                    R$
                  </span>
                  <Input
                    value={faixa.label}
                    onChange={(e) =>
                      updateFaixa(faixa.id, "label", e.target.value)
                    }
                    className="h-7 text-[11px] flex-1 min-w-0 bg-muted/30 border-border/50"
                    placeholder="Ex: 1 a 3"
                  />
                </div>
                <Input
                  value={faixa.valor}
                  onChange={(e) =>
                    updateFaixa(faixa.id, "valor", e.target.value)
                  }
                  className="h-7 w-24 text-[11px] text-right font-mono shrink-0"
                />
                <button
                  type="button"
                  onClick={() => removeFaixa(faixa.id)}
                  className="shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const ParametrizacaoPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [tabela, setTabela] = useState<TabelaFaturamento>(DEFAULT_TABELA);
  const [criterios, setCriterios] = useState<CriterioAdicional[]>(
    DEFAULT_CRITERIOS_ADICIONAIS,
  );
  const [_planos, _setPlanos] = useState<PlanoConfig[]>([]);
   
  const [_crit, _setCrit] = useState<CriterioConfig[]>([]);

  // Load existing DB data (for planos etc.)
  const fetchData = useCallback(async () => {
    try {
      const [planRes] = await Promise.all([
        dataProvider
          .getList<PlanoConfig>("plano_configuracao", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "ordem", order: "ASC" },
            filter: {},
          })
          .catch(() => ({ data: [] })),
      ]);
      if (planRes.data && planRes.data.length > 0) _setPlanos(planRes.data);
      else _setPlanos(DEFAULT_PLANOS);
    } catch {
      _setPlanos(DEFAULT_PLANOS);
    }
  }, [dataProvider]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCriterio = () => {
    const novo: CriterioAdicional = {
      id: uid(),
      nome: "Novo Critério",
      descricao: "Descrição do critério",
      tipo: "faixa",
      planos: ["basico", "intermediario", "avancado"],
      vincularPlanos: false,
      faixas: [{ id: uid(), label: "Faixa 1", valor: "0,00" }],
    };
    setCriterios((prev) => [...prev, novo]);
  };

  const handleSave = () => {
    notify("Parametrização salva com sucesso!", { type: "success" });
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-full">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Parametrização</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure as tabelas de precificação e critérios adicionais de
            honorários.
          </p>
        </div>
        <Button size="sm" onClick={handleSave} className="gap-2 text-xs">
          <Check className="w-3.5 h-3.5" />
          Salvar Parametrização
        </Button>
      </div>

      {/* ───── 2.1 FAIXAS DE FATURAMENTO ───── */}
      <div className="space-y-2">
        <div className="flex items-start gap-1">
          <div>
            <h2 className="text-sm font-bold text-foreground">
              2.1 - Critérios de Precificação
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Os demais planos{" "}
              <strong>(INTERMEDIÁRIO (RECOMENDADO) e AVANÇADO)</strong> serão
              calculados automaticamente tomando os valores que você configurar
              em cada plano na etapa anterior.
            </p>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-xl p-4 space-y-4">
          <div className="text-center text-[11px] text-muted-foreground font-medium">
            Escolha abaixo quais atividades deseja utilizar
          </div>
          <FaixasFaturamentoSection tabela={tabela} onChange={setTabela} />
        </div>
      </div>

      {/* ───── 2.2 CRITÉRIOS ADICIONAIS ───── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-foreground">
              2.2 - Critérios Adicionais
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Estes critérios serão somados ao valor do plano definido com base
              nas faixas de faturamento. Adicione todos os itens que possuem
              variação volumétrica e que podem dar mais ou menos trabalho para a
              operação no dia-a-dia.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={addCriterio}
              className="gap-1.5 text-xs h-8"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Critério
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              Ordenar Critérios
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {criterios.map((c) => (
            <CriterioCard
              key={c.id}
              criterio={c}
              onChange={(updated) =>
                setCriterios((prev) =>
                  prev.map((x) => (x.id === c.id ? updated : x)),
                )
              }
              onDelete={() =>
                setCriterios((prev) => prev.filter((x) => x.id !== c.id))
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};
